import { Suspense, FC, createContext, lazy, useEffect } from "react";
import { Switch, Route, withRouter, RouteComponentProps } from "react-router-dom";
import { Formik, FormikConfig, FormikHelpers } from "formik";
import * as _ from "lodash";

import { Enterprise, useTranslation } from "@ctra/i18n";
import { GamePlanFormValues } from "@ctra/api";
import { useGoogleAnalytics } from "@ctra/analytics";
import { capitalCase } from "@ctra/utils";

import {
  Steps,
  Button,
  Row,
  Col,
  Grid,
  LeftOutlined,
  CompassOutlined,
  BarChartOutlined,
  CheckSquareOutlined,
  ProfileOutlined,
  Skeleton,
  Spin,
  Form,
  Space,
  CtraLayout
} from "@ctra/components";

import { Routes } from "@routes";
import { PortalContext } from "@base";
import { ScorecardContext } from "@scorecard";

import { GAActions } from "../../analytics";
import { FormEffects } from "../../providers";
import { useSteps, useValidationSchema } from "./hooks";
import styles from "./Wizard.module.less";

const General = lazy(() => import("./steps/General"));
const Tracking = lazy(() => import("./steps/Tracking"));
const Tasks = lazy(() => import("./steps/Tasks"));
const Summary = lazy(() => import("./steps/Summary"));

const { useBreakpoint } = Grid;
const { Step } = Steps;
const { ContentWrapper } = CtraLayout;

export enum WizardDomain {
  /**
   * use with a built-in farm selector
   */
  Global = "global",
  /**
   * farm is preset
   */
  Farm = "farm"
}

export enum WizardIntent {
  /**
   * create game plan
   */
  Create = "create",
  /**
   * edit game plan
   */
  Edit = "edit"
}

type Path = typeof Routes.app.gamePlans.create | typeof Routes.app.gamePlans.gamePlan.edit;

type WizardProps = Omit<FormikConfig<GamePlanFormValues>, "onSubmit"> &
  RouteComponentProps & {
    /**
     * limited to a farm or global
     */
    domain: WizardDomain;
    /**
     * wizard variant
     */
    intent: WizardIntent;
    /**
     * create or edit paths following the shape of the routes
     */
    paths: Path;
    /**
     * spinner on/off
     */
    spinning?: boolean;
    /**
     * submit the game plan
     * @param values
     */
    onSubmit: (values: GamePlanFormValues, formikHelpers: FormikHelpers<GamePlanFormValues>) => void;
    /**
     * handle closing the wizard
     */
    onClose?: () => void;
  };

/**
 * Make a context for the wizard API to be able to navigate from the individual pages
 */
export const WizardContext = createContext<Pick<ReturnType<typeof useSteps>[1], "previous" | "next">>({
  previous: () => console.log("previous step"),
  next: () => console.log("next step")
});

/**
 * Wizard for creating or editing a game plan
 * @param domain
 * @param variant
 * @param paths
 * @param staticContext
 * @param history
 * @param location
 * @param match
 * @param formikProps
 * @constructor
 */
export const Wizard = withRouter<WizardProps, FC<WizardProps>>(
  ({
    domain,
    intent,
    spinning = false,
    paths,
    onSubmit,
    staticContext,
    history,
    location,
    match,
    ...formikProps
  }) => {
    const { t } = useTranslation();
    const validationSchema = useValidationSchema();
    const { md } = useBreakpoint();
    const { trackEvent } = useGoogleAnalytics();

    /**
     * Collect which paths bundle which required fields, so in case
     * of errors, we can highlight the corresponding step.
     */
    const validatingFields: Record<keyof typeof paths, Array<keyof GamePlanFormValues>> = {
      general: ["farmID", "title", "timePeriod"],
      tracking: ["dataDescriptorID"],
      tasks: ["taskList"],
      summary: [],
      index: []
    };

    const [{ steps, currentStep }, { getStatus, previous, next, jump }] = useSteps<
      typeof paths,
      GamePlanFormValues
    >(paths, ["general", "tracking", "tasks", "summary"], validatingFields);

    /**
     * Icons for each step
     */
    const icons = [CompassOutlined, BarChartOutlined, CheckSquareOutlined, ProfileOutlined];

    /**
     * Add the icons to the steps
     */
    const withIcons = _.zipWith(steps, icons, (step, icon) => ({ ...step, icon }));

    const { form, step } = Enterprise.gamePlan.wizard;

    /**
     * Warn the user of losing the progress before refresh
     */
    useEffect(() => {
      window.onbeforeunload = function () {
        return window.confirm();
      };

      return () => {
        window.onbeforeunload = _.noop;
      };
    }, []);

    return (
      <PortalContext.Provider value={{ portal: "game-plan-wizard-portal" }}>
        <Spin spinning={spinning} tip={t<string>(form.extra.submitting)}>
          <ContentWrapper>
            {/* @todo only add props here if they apply to both creating & editing a game plan */}
            <Formik validationSchema={validationSchema} onSubmit={onSubmit} {...formikProps}>
              {(formikProps) => {
                /**
                 * Fields of the current step
                 * e.g. General: farmID, title, timePeriod
                 */
                const currentStepFields = _.values(validatingFields)[currentStep];

                /**
                 * before moving to the next step, touch fields of current step
                 * not done in the onClick method errors can be evaluated on click
                 */
                const handleMouseDown = () =>
                  formikProps.setTouched({
                    ..._.reduce(
                      currentStepFields,
                      (obj, item) => ({
                        ...obj,
                        [item]: !!item
                      }),
                      {}
                    )
                  });

                /**
                 * Errors of current step
                 */
                const currentErrors = _.pick(formikProps.errors, currentStepFields);

                return (
                  <>
                    <WizardContext.Provider value={{ previous, next }}>
                      <ScorecardContext.Provider
                        dataDescriptorID={formikProps.values.dataDescriptorID as string}
                        farmID={formikProps.values.farmID as number}
                      >
                        <FormEffects>
                          {/* Using style to force direction. This is a known ant design issue. */}
                          <div onMouseDown={handleMouseDown}>
                            <Steps
                              current={currentStep}
                              className={styles.Steps}
                              onChange={(newStep) =>
                                /* can either move backwards or forward only if there are no errors */
                                (_.isEmpty(currentErrors) || currentStep > newStep) && jump(newStep)
                              }
                            >
                              {_.map(withIcons, ({ key, path, icon: Icon }) => (
                                <Step
                                  icon={<Icon />}
                                  key={key}
                                  status={getStatus(path, formikProps)}
                                  title={md ? t<string>(step[key]) : ""}
                                  onClick={() => {
                                    /* istanbul ignore next */
                                    trackEvent(GAActions.goToStep, { label: capitalCase(key) });
                                  }}
                                />
                              ))}
                            </Steps>
                          </div>
                          <Form className={styles.Form} requiredMark="optional" layout="vertical">
                            <Suspense fallback={<Skeleton />}>
                              <Switch>
                                <Route path={paths.general}>
                                  <General withFarmSelect={domain === WizardDomain.Global} />
                                </Route>
                                <Route path={paths.tracking}>
                                  <Tracking />
                                </Route>
                                <Route path={paths.tasks}>
                                  <Tasks />
                                </Route>
                                <Route path={paths.summary}>
                                  <Summary />
                                </Route>
                              </Switch>
                            </Suspense>
                          </Form>
                        </FormEffects>
                      </ScorecardContext.Provider>
                    </WizardContext.Provider>
                    <Row gutter={[16, 0]}>
                      <Col flex={1}>
                        {currentStep > 0 && (
                          <Button
                            data-testid="game-plan-wizard-prev-button"
                            icon={<LeftOutlined />}
                            size="large"
                            onClick={() => {
                              /* istanbul ignore next */
                              trackEvent(GAActions.previousStep, {
                                label: capitalCase(steps[currentStep - 1]?.key)
                              });
                              previous();
                            }}
                          >
                            {md ? t<string>(step.previous) : ""}
                          </Button>
                        )}
                      </Col>
                      <Col flex={1} className={styles.Submit}>
                        <Space direction="horizontal">
                          <PortalContext.Consumer>
                            {({ portal }) => <div id={portal} />}
                          </PortalContext.Consumer>
                          {currentStep < steps.length - 1 && (
                            <Button
                              data-testid="game-plan-wizard-next-button"
                              size="large"
                              type="primary"
                              onMouseDown={handleMouseDown}
                              onClick={() => {
                                /* istanbul ignore next */
                                trackEvent(GAActions.nextStep, {
                                  label: capitalCase(steps[currentStep + 1]?.key)
                                });
                                /* can move ahead if there are no errors */
                                _.isEmpty(currentErrors) && next();
                              }}
                            >
                              {t<string>(step.next)}
                            </Button>
                          )}
                        </Space>
                      </Col>
                    </Row>
                  </>
                );
              }}
            </Formik>
          </ContentWrapper>
        </Spin>
      </PortalContext.Provider>
    );
  }
);
